import { useCallback, useEffect, useState } from 'react';
import { PrefSwitch } from '../components/PrefControls';
import {
  DISCUSSION_NOTIFICATION_PREFS_CHANGED_EVENT,
  readDiscussionNotificationPrefs,
  readDiscussionNotificationPromptDismissed,
  readDiscussionNotificationSoundEnabled,
  readDiscussionNotificationSystemEnabled,
  readDiscussionNotificationToastEnabled,
  writeDiscussionNotificationSoundEnabled,
  writeDiscussionNotificationSystemEnabled,
  writeDiscussionNotificationToastEnabled
} from '../notifications/discussionNotificationPrefs';
import {
  ensureDiscussionNotificationServiceWorker,
  getSystemNotificationPermission,
  requestSystemNotificationPermission,
  showDiscussionTestSystemNotification
} from '../notifications/discussionSystemNotification';
import { showErrorToast, showSuccessToast, showWarningToast } from '../notifications/toast';
import '../theme/themePreferences.css';

function permissionStatusLabel(permission: NotificationPermission | 'unsupported'): string {
  if (permission === 'unsupported') return 'Desktop notifications are not supported in this browser.';
  if (permission === 'granted') return 'Desktop notification permission granted.';
  if (permission === 'denied') return 'Desktop notifications are blocked. Enable them in your browser site settings.';
  return 'Desktop notification permission has not been requested yet.';
}

export function DiscussionAlertsPreferencesPanel() {
  const [soundEnabled, setSoundEnabled] = useState(() => readDiscussionNotificationSoundEnabled());
  const [toastEnabled, setToastEnabled] = useState(() => readDiscussionNotificationToastEnabled());
  const [systemEnabled, setSystemEnabled] = useState(() => readDiscussionNotificationSystemEnabled());
  const [systemPermission, setSystemPermission] = useState(() => getSystemNotificationPermission());
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    const sync = () => {
      const prefs = readDiscussionNotificationPrefs();
      setSoundEnabled(prefs.soundEnabled);
      setToastEnabled(prefs.toastEnabled);
      setSystemEnabled(prefs.systemEnabled);
      setSystemPermission(getSystemNotificationPermission());
    };
    window.addEventListener(DISCUSSION_NOTIFICATION_PREFS_CHANGED_EVENT, sync);
    return () => window.removeEventListener(DISCUSSION_NOTIFICATION_PREFS_CHANGED_EVENT, sync);
  }, []);

  useEffect(() => {
    if (!systemEnabled || systemPermission !== 'granted') return;
    void ensureDiscussionNotificationServiceWorker();
  }, [systemEnabled, systemPermission]);

  const onSoundChanged = useCallback((value: boolean) => {
    setSoundEnabled(value);
    writeDiscussionNotificationSoundEnabled(value);
  }, []);

  const onToastChanged = useCallback((value: boolean) => {
    setToastEnabled(value);
    writeDiscussionNotificationToastEnabled(value);
  }, []);

  const onSystemChanged = useCallback((value: boolean) => {
    setSystemEnabled(value);
    writeDiscussionNotificationSystemEnabled(value);
    if (value) {
      void requestSystemNotificationPermission().then(async (permission) => {
        setSystemPermission(permission);
        if (permission === 'granted') {
          await ensureDiscussionNotificationServiceWorker();
        }
      });
    } else {
      setSystemPermission(getSystemNotificationPermission());
    }
  }, []);

  const onTestSystemNotification = useCallback(async () => {
    if (testing) return;
    setTesting(true);
    try {
      let permission = getSystemNotificationPermission();
      if (permission === 'default') {
        permission = await requestSystemNotificationPermission();
        setSystemPermission(permission);
      }
      if (permission !== 'granted') {
        showWarningToast(
          permission === 'denied'
            ? 'Desktop notifications are blocked in the browser site settings.'
            : 'Desktop notifications are not available in this browser.'
        );
        return;
      }

      await ensureDiscussionNotificationServiceWorker();
      const result = await showDiscussionTestSystemNotification();
      if (result.ok) {
        showSuccessToast(
          result.via === 'service-worker'
            ? 'Test notification sent. Check the Windows notification banner or Action Center.'
            : 'Test notification sent. If no banner appears, check Windows Focus assist and Edge notification settings.'
        );
        return;
      }
      showErrorToast(result.message);
    } finally {
      setTesting(false);
    }
  }, [testing]);

  return (
    <section className="theme-preferences" aria-labelledby="discussion-alerts-preferences-heading">
      <h3 id="discussion-alerts-preferences-heading" className="theme-preferences__title">
        Discussion alerts
      </h3>
      <p className="theme-preferences__hint">
        Sound, toast, and desktop notifications for discussion activity in document viewers and record
        popups. When you are away, @mentions still send email notifications.
      </p>
      <div className="theme-preferences__section">
        <div className="theme-preferences__switch-stack">
          <div className="theme-preferences__compact-row">
            <PrefSwitch checked={soundEnabled} onChange={onSoundChanged} aria-label="Play sound on new activity" />
            <span className="theme-preferences__hint theme-preferences__hint--inline">
              Play sound on new activity
            </span>
          </div>
          <div className="theme-preferences__compact-row">
            <PrefSwitch checked={toastEnabled} onChange={onToastChanged} aria-label="Show in-app toast" />
            <span className="theme-preferences__hint theme-preferences__hint--inline">
              Show in-app toast for 10 seconds
            </span>
          </div>
          <div className="theme-preferences__compact-row">
            <PrefSwitch checked={systemEnabled} onChange={onSystemChanged} aria-label="Show desktop notifications" />
            <span className="theme-preferences__hint theme-preferences__hint--inline">
              Show desktop notifications
            </span>
          </div>
        </div>
        <p className="theme-preferences__hint theme-preferences__hint--tight">
          {permissionStatusLabel(systemPermission)}
        </p>
        {systemPermission === 'granted' ? (
          <button
            type="button"
            className="theme-preferences__action-btn"
            onClick={() => void onTestSystemNotification()}
            disabled={testing}
          >
            {testing ? 'Sending…' : 'Test desktop notification'}
          </button>
        ) : null}
        {systemPermission === 'granted' ? (
          <p className="theme-preferences__hint theme-preferences__hint--tight">
            Edge may hide banners while this tab is focused or when Windows Focus assist is on — check the
            notification center if you do not see a pop-up.
          </p>
        ) : null}
        {readDiscussionNotificationPromptDismissed() ? null : (
          <p className="theme-preferences__hint theme-preferences__hint--tight">
            You will be asked once when you open a discussion panel.
          </p>
        )}
      </div>
    </section>
  );
}
